import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router'

/**
 * URL クエリパラメータで状態（主にビューア/ページの表示設定）を管理する共通フック群。
 *
 * 共通規約:
 * - 不正な値が URL に入っていた場合はデフォルト値にフォールバックする
 * - デフォルト値と同じときはパラメータを URL から取り除く（URL を短く保つ）
 * - 履歴を汚さないよう既定では replace で更新する
 */

/**
 * クエリ文字列と値の相互変換。
 * parse / serialize はメソッド構文で定義し、複数フィールドをまとめて扱う際に
 * 型パラメータの分散で詰まらないようにしている。
 */
export type QueryCodec<T> = {
  parse(raw: string | null): T
  serialize(value: T): string
  defaultValue: T
}

export type QuerySetter<T> = (value: T | ((prev: T) => T)) => void

type QueryStateOptions = {
  /** true: 履歴を積まずに置き換える（デフォルト） */
  replace?: boolean
}

/** 許可値の一覧を持つ文字列（enum 相当）用のコーデック */
export const enumCodec = <T extends string>(values: readonly T[], defaultValue: T): QueryCodec<T> => ({
  parse: (raw) => values.find((value) => value === raw) ?? defaultValue,
  serialize: (value) => value,
  defaultValue,
})

/** 数値用のコーデック。範囲クランプと小数丸めに対応 */
export const numberCodec = (
  defaultValue: number,
  options: { min?: number; max?: number; precision?: number } = {},
): QueryCodec<number> => {
  const { min = -Infinity, max = Infinity, precision } = options
  const normalize = (value: number): number => {
    const clamped = Math.min(max, Math.max(min, value))
    if (precision === undefined) return clamped
    const factor = 10 ** precision
    return Math.round(clamped * factor) / factor
  }

  return {
    parse: (raw) => {
      if (raw === null) return defaultValue
      const numeric = Number(raw)
      return Number.isFinite(numeric) ? normalize(numeric) : defaultValue
    },
    serialize: (value) => String(normalize(value)),
    defaultValue,
  }
}

/** 真偽値用のコーデック（1 / 0 で表現） */
export const booleanCodec = (defaultValue: boolean): QueryCodec<boolean> => ({
  parse: (raw) => {
    if (raw === null) return defaultValue
    return raw === '1' || raw === 'true'
  },
  serialize: (value) => (value ? '1' : '0'),
  defaultValue,
})

/** 文字列用のコーデック */
export const stringCodec = (defaultValue = ''): QueryCodec<string> => ({
  parse: (raw) => raw ?? defaultValue,
  serialize: (value) => value,
  defaultValue,
})

/** デフォルト値と同じならパラメータを削除、違えばセットする */
const applyParam = <T>(params: URLSearchParams, name: string, codec: QueryCodec<T>, value: T): void => {
  const serialized = codec.serialize(value)
  if (serialized === codec.serialize(codec.defaultValue)) {
    params.delete(name)
    return
  }
  params.set(name, serialized)
}

/**
 * 単一のクエリパラメータを state のように扱うフック。
 *
 * codec は再レンダリングのたびに作り直さないよう、モジュールスコープの定数か
 * useMemo で保持したものを渡すこと。
 *
 * @example
 * const DIRECTION = enumCodec(['ttb', 'rtl', 'ltr'] as const, 'ttb')
 * const [direction, setDirection] = useQueryState('direction', DIRECTION)
 */
export const useQueryState = <T>(
  name: string,
  codec: QueryCodec<T>,
  { replace = true }: QueryStateOptions = {},
): [T, QuerySetter<T>] => {
  const [searchParams, setSearchParams] = useSearchParams()

  const value = useMemo(() => codec.parse(searchParams.get(name)), [searchParams, name, codec])

  const setValue = useCallback<QuerySetter<T>>(
    (next) => {
      setSearchParams(
        (prev) => {
          // prev は react-router がメモ化して返しているオブジェクトなので、破壊せずコピーを更新する
          const params = new URLSearchParams(prev)
          const resolved = typeof next === 'function' ? (next as (prev: T) => T)(codec.parse(params.get(name))) : next
          applyParam(params, name, codec, resolved)
          return params
        },
        { replace },
      )
    },
    [setSearchParams, name, codec, replace],
  )

  return [value, setValue]
}

/** 設定オブジェクトの各キーと、クエリパラメータ名 / コーデックの対応 */
export type QuerySchema<S> = {
  [K in keyof S]: { param: string; codec: QueryCodec<S[K]> }
}

type UnknownField = { param: string; codec: QueryCodec<unknown> }

const parseSettings = <S extends object>(schema: QuerySchema<S>, params: URLSearchParams): S => {
  const fields = Object.entries(schema) as [keyof S, UnknownField][]
  const result = {} as S
  for (const [key, { param, codec }] of fields) {
    result[key] = codec.parse(params.get(param)) as S[keyof S]
  }
  return result
}

const applySettings = <S extends object>(schema: QuerySchema<S>, params: URLSearchParams, value: S): void => {
  const fields = Object.entries(schema) as [keyof S, UnknownField][]
  for (const [key, { param, codec }] of fields) {
    applyParam(params, param, codec, value[key])
  }
}

/**
 * 複数のクエリパラメータを 1 つの設定オブジェクトとして扱うフック。
 *
 * schema はモジュールスコープの定数として定義すること。
 *
 * @example
 * const SCHEMA = { zoom: { param: 'zoom', codec: numberCodec(1, { min: 0.3, max: 5 }) } }
 * const [settings, setSettings] = useQuerySettings(SCHEMA)
 */
export const useQuerySettings = <S extends object>(
  schema: QuerySchema<S>,
  { replace = true }: QueryStateOptions = {},
): [S, QuerySetter<S>] => {
  const [searchParams, setSearchParams] = useSearchParams()

  const settings = useMemo(() => parseSettings(schema, searchParams), [schema, searchParams])

  const setSettings = useCallback<QuerySetter<S>>(
    (next) => {
      setSearchParams(
        (prev) => {
          // prev は react-router がメモ化して返しているオブジェクトなので、破壊せずコピーを更新する
          const params = new URLSearchParams(prev)
          const resolved = typeof next === 'function' ? (next as (prev: S) => S)(parseSettings(schema, params)) : next
          applySettings(schema, params, resolved)
          return params
        },
        { replace },
      )
    },
    [setSearchParams, schema, replace],
  )

  return [settings, setSettings]
}
